import { FC, PropsWithChildren, ReactChild } from 'react'

import { styled, css } from 'styled-components'

import Skeleton from 'core/components/lib/Skeleton'
import Spinner from 'core/components/lib/Spinner'
import { outline } from 'core/styles'

type FormContainerProps = PropsWithChildren<{
  title: string
  subTitle?: string
  customClassName?: string
  borderless?: boolean
  style?: object
  /** If included, there will be a help tooltip next
   * to the title with this text in it. If no title is
   * provided, there is no help tooltip.
   */
  helpText?: ReactChild
  helpTextMargin?: string
  removeHelpTextTitle?: boolean
  iconMargin?: string
  /**
   * @deprecated
   * The parent component can pass the loading status stored
   * in redux for the api call here to show a message at the
   * bottom of the InfoCard.
   */
  status?: 'success' | 'loading' | 'error'
  /** The message that should be displayed at the bottom of
   * the InfoCard when `status` is set to "success".
   */
  successMessage?: string
  /** If status is provided, loanId is needed to make the link
   * in the status message to return to the activity page. We
   * should eventually refactor this to no longer require the
   * loanId be passed in but instead just use useParams or
   * withRouter to supply it.
   */
  errorMessage?: string
  statusIcon?: ReactChild
  helpTitle?: string
  isLoading?: boolean
  isSuccess?: boolean
  isSubmitting?: boolean
}>

const FormContainer: FC<FormContainerProps> = ({
  children,
  // successMessage = 'Action complete',
  borderless = true,
  isLoading,
  isSubmitting,
  // isSuccess,
  // status,
  // ...props
}) => (
  <Container
    $borderless={borderless}
    // status={status ? status : isSuccess ? 'success' : undefined}
    // successMessage={successMessage}
  >
    {isLoading ? (
      <Skeleton height='560px' width='600px'>
        {/* Row 1 */}
        \n <rect height='28' width='150' x='20' y='18' />
        \n <rect height='28' width='280' x='220' y='18' />
        \n <rect height='28' width='28' x='540' y='18' />
        \n <rect height='1' width='560' x='20' y='76' />
        {/* Row 2 */}
        \n <rect height='28' width='150' x='20' y='104' />
        \n <rect height='28' width='280' x='220' y='104' />
        \n <rect height='28' width='28' x='540' y='104' />
        \n <rect height='1' width='560' x='20' y='162' />
        {/* Row 3 */}
        \n <rect height='28' width='150' x='20' y='190' />
        \n <rect height='28' width='280' x='220' y='190' />
        \n <rect height='28' width='28' x='540' y='190' />
        \n <rect height='1' width='560' x='20' y='248' />
        {/* Row 4 */}
        \n <rect height='28' width='150' x='20' y='276' />
        \n <rect height='28' width='280' x='220' y='276' />
        \n <rect height='28' width='28' x='540' y='276' />
        \n <rect height='1' width='560' x='20' y='334' />
        {/* Submit Row */}
        \n <rect height='28' width='42' x='400' y='362' />
        \n <rect height='28' width='105' x='465' y='362' />
      </Skeleton>
    ) : (
      <>
        <Content $isSubmitting={isSubmitting}>{children}</Content>
        {isSubmitting && (
          <SpinnerContainer>
            <Spinner />
          </SpinnerContainer>
        )}
      </>
    )}
  </Container>
)

export default FormContainer

const Container = styled.section<{
  $borderless?: boolean
}>`
  box-sizing: border-box;
  position: relative;
  border-radius: 8px;

  & + & {
    margin-top: 16px;
  }

  ${(p) =>
    p.$borderless
      ? css`
          border: none;
          overflow: visible;
        `
      : css`
          border: 1px solid ${outline};
        `}
`

const SpinnerContainer = styled.div`
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`

const Content = styled.div<{ $isSubmitting?: boolean }>`
  opacity: ${(p) => (p.$isSubmitting ? 0.5 : 1)};
`
